import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { apiUrl } from '../../../environments/environment';

/**
 * Read and sign your own agreement — **with no login**.
 *
 * ── Why there is no guard ────────────────────────────────────────────────
 * A contract goes to somebody who may have no account at all — a candidate
 * certainly does not. And `WithoutAuthGuard` would be wrong too: it turns away
 * LOGGED-IN people, so HR could not even open the link they sent. The same
 * reasoning is written on the public survey (`/s/:slug`).
 *
 * ── This page knows nothing ──────────────────────────────────────────────
 * No org, no reg_id, no contract id. Only the token. The server decides all the
 * rest — an id arriving in the request is never trusted.
 *
 * ── The paper looks exactly as it was sent ───────────────────────────────
 * The server sends `body_snapshot`, not a fresh render from the template. Even
 * if the template changed later, what is being signed is what must be shown —
 * and that is what later becomes the evidence.
 */
@Component({
  selector: 'app-contract-sign',
  templateUrl: './contract-sign.component.html',
  styleUrls: ['./contract-sign.component.scss']
})
export class ContractSignComponent implements OnInit {

  token = '';
  loading = true;
  busy = false;

  error: string | null = null;
  contract: any = null;
  html: SafeHtml | null = null;
  canSign = false;
  reason: string | null = null;

  typed = '';
  agree = false;
  formError: string | null = null;

  done = false;
  doneAt: string | null = null;
  declined = false;

  showDecline = false;
  declineReason = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  async ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    await this.load();
  }

  private post(path: string, body: any) {
    return this.http.post<any>(apiUrl + '/api/public/contract/' + path, body).toPromise();
  }

  async load() {
    this.loading = true;
    try {
      const r: any = await this.post('view', { token: this.token });
      if (!r || r.status !== 1) {
        this.error = (r && r.msg) || 'This link is not valid.';
      } else {
        this.contract = r.contract;
        this.html = this.sanitizer.bypassSecurityTrustHtml(r.contract.html);
        this.canSign = !!r.can_sign;
        this.reason = r.reason;
        if (r.contract.state === 'signed') { this.done = true; this.doneAt = r.contract.signed_at; }
        if (r.contract.state === 'declined') this.declined = true;
      }
    } catch (e) {
      this.error = 'We could not open this document. Please try again in a moment.';
    }
    this.loading = false;
  }

  /** Why it cannot be signed — each reason has its own meaning, not one "invalid". */
  get blockedMessage(): string | null {
    switch (this.reason) {
      case 'signed':     return null;                       // it has its own separate panel
      case 'declined':   return 'This agreement was declined. Your organisation can send a fresh one.';
      case 'superseded': return 'This version has been replaced. Look for the newer email, or ask your organisation for it.';
      case 'void':       return 'This agreement was withdrawn. Please ask your organisation for a new one.';
      case 'expired':    return 'This link has expired. Ask your organisation to send it again — it takes them one click.';
      case 'not_sent':   return 'This agreement is not ready to sign yet.';
      default:           return null;
    }
  }

  async sign() {
    this.formError = null;
    if (!this.typed.trim()) { this.formError = 'Please type your full name.'; return; }
    if (!this.agree) { this.formError = 'Please confirm that you have read and accept the terms.'; return; }

    this.busy = true;
    try {
      const r: any = await this.post('sign', { token: this.token, name: this.typed, agree: true });
      if (!r || r.status !== 1) { this.formError = (r && r.msg) || 'Could not record your signature.'; }
      else { this.done = true; this.doneAt = r.at; this.canSign = false; }
    } catch (e) {
      this.formError = 'Could not record your signature. Please try again.';
    }
    this.busy = false;
  }

  async decline() {
    this.busy = true;
    try {
      const r: any = await this.post('decline', { token: this.token, reason: this.declineReason });
      if (!r || r.status !== 1) { this.formError = (r && r.msg) || 'Could not record that.'; }
      else { this.declined = true; this.canSign = false; this.showDecline = false; }
    } catch (e) {
      this.formError = 'Could not record that. Please try again.';
    }
    this.busy = false;
  }

  /**
   * Their own copy — the same PDF HR has.
   *
   * There used to be only `window.print()`. That comes out differently in every
   * browser, is often useless on mobile, and does not carry the signature block
   * that matters later. Both sides are now built by one renderer.
   */
  get pdfUrl(): string {
    return apiUrl + '/api/public/contract/pdf?token=' + encodeURIComponent(this.token);
  }

  print() { window.print(); }
}
